// client/src/utils/timeZones.ts (ST-00273)
//
// IANA time zones for pickers: the browser's list (Intl.supportedValuesOf('timeZone'), with
// a fallback list for browsers without it), the common zones first, and labels with each
// zone's current UTC offset ("Africa/Johannesburg (UTC+02:00)"). The server checks the same
// thing (server/utils/timeZones.ts): a name the IANA database knows, not an offset.

/** The mining time zone the app reports in (SAST). */
export const DEFAULT_TIME_ZONE = 'Africa/Johannesburg';

/** Shown at the top of a zone picker: where LexiBridge's customers and partners are. */
export const COMMON_TIME_ZONES = [
  'Africa/Johannesburg',
  'UTC',
  'Africa/Windhoek',
  'Africa/Gaborone',
  'Africa/Maputo',
  'Africa/Harare',
  'Africa/Lusaka',
  'Africa/Lubumbashi',
  'Europe/London',
  'Australia/Perth',
] as const;

// For a browser without Intl.supportedValuesOf: the common zones and the main zone of
// each region.
const FALLBACK_TIME_ZONES = [
  ...COMMON_TIME_ZONES,
  'Africa/Cairo',
  'Africa/Lagos',
  'Africa/Nairobi',
  'Africa/Kinshasa',
  'Africa/Luanda',
  'Africa/Dar_es_Salaam',
  'Africa/Accra',
  'Africa/Casablanca',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'America/Sao_Paulo',
  'America/Santiago',
  'America/Lima',
  'America/Mexico_City',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Jakarta',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Europe/Amsterdam',
  'Europe/Berlin',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/Moscow',
  'Europe/Paris',
  'Indian/Mauritius',
  'Pacific/Auckland',
];

const ZONE_NAME = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/;

let cached: string[] | null = null;

/** Every IANA zone the browser knows, sorted, with UTC (some engines leave it out). */
export function allTimeZones(): string[] {
  if (cached) return cached;
  let zones: string[] = [];
  try {
    const supported = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf;
    zones = typeof supported === 'function' ? supported('timeZone') : [];
  } catch {
    zones = [];
  }
  if (!zones.length) zones = FALLBACK_TIME_ZONES;
  cached = [...new Set([...zones, 'UTC'])].sort((a, b) => a.localeCompare(b));
  return cached;
}

/** A name the IANA database knows ('Africa/Johannesburg', 'UTC', a link such as 'US/Eastern'). */
export function isKnownTimeZone(tz: unknown): tz is string {
  if (typeof tz !== 'string' || !ZONE_NAME.test(tz.trim())) return false;
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz.trim() });
    return true;
  } catch {
    return false;
  }
}

/** The list's spelling of a zone typed in another case ('africa/johannesburg'); else as given. */
export function canonicalTimeZone(tz: string): string {
  const t = tz.trim();
  if (!isKnownTimeZone(t)) return tz;
  try {
    const resolved = new Intl.DateTimeFormat('en', { timeZone: t }).resolvedOptions().timeZone;
    return resolved.toLowerCase() === t.toLowerCase() ? resolved : t;
  } catch {
    return t;
  }
}

/** The zone's UTC offset at `at` (today by default), e.g. 'UTC+02:00', 'UTC-03:30', 'UTC+00:00'. */
export function utcOffsetLabel(tz: string, at: Date = new Date()): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'longOffset' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName')?.value;
    if (part) {
      const m = /^GMT(?:([+-])(\d{1,2})(?::?(\d{2}))?)?$/.exec(part);
      if (m) {
        if (!m[1]) return 'UTC+00:00';
        return `UTC${m[1]}${m[2]!.padStart(2, '0')}:${m[3] || '00'}`;
      }
    }
  } catch {
    /* unknown zone */
  }
  // No longOffset support: work the offset out from the zone's wall clock.
  try {
    const wall = new Date(at.toLocaleString('en-US', { timeZone: tz }));
    const utc = new Date(at.toLocaleString('en-US', { timeZone: 'UTC' }));
    const mins = Math.round((wall.getTime() - utc.getTime()) / 60000);
    const sign = mins < 0 ? '-' : '+';
    const abs = Math.abs(mins);
    return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
  } catch {
    return '';
  }
}

/** 'Africa/Johannesburg (UTC+02:00)'. */
export function timeZoneLabel(tz: string, at?: Date): string {
  const offset = utcOffsetLabel(tz, at);
  return offset ? `${tz} (${offset})` : tz;
}
